import React, { useState, useCallback, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import axios from "axios";
import { Button } from "devextreme-react/button";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";
import { LoadPanel } from "devextreme-react/load-panel";
import Toolbar, { Item } from "devextreme-react/toolbar";
import ValidationSummary from "devextreme-react/validation-summary";
import notify from "devextreme/ui/notify";
import { custom, confirm } from "devextreme/ui/dialog";
import routes from "../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../common/Messages";
import { EMPTY_GUID, LaborTrackingRoutes, LaborTrackingEntryModes } from "../../../const/constants";
import PrimaryTab from "./tabs/PrimaryTab";

const initialFormFields = {
    rowid: EMPTY_GUID,
    EntryMode: null,
    StartDate: null,
    EndDate: null,
    ElapsedHours: null,
    ElapsedMinutes: null,
    RWorker: null,
    RSourceId: null,
    RCustomer: null,
    RGldepartment: null,
    RCaactivity: null,
    WorkUnitUOM: null,
    WorkUnitQuantity: null,
    UserDefinedQuantity1: null,
    UserDefinedQuantity2: null,
    UserDefinedQuantity3: null,
    UserDefinedCode1: null,
    UserDefinedCode2: null,
    UserDefinedCode3: null,
    UserDefinedReference1: null,
    UserDefinedReference2: null,
    UserDefinedReference3: null,
    Comment: null,
};

function LaborTrackingForm(props) {
    const history = useHistory();
    const location = useLocation();
    // Prefer the routed prop, but fall back to the query string so a hard page load
    // (before the router settles the prop) still loads the record.
    const editRowId = props.id || new URLSearchParams(location.search).get("id");
    const accessToken = props.oidc.user.access_token;
    const [formFields, setformFields] = useState(initialFormFields);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const formFieldsRef = useRef(formFields);
    const entryModesRef = useRef(LaborTrackingEntryModes);
    const primaryFormRefs = useRef(null);

    const headers = {
        access_token: accessToken,
        Authorization: `Bearer ${accessToken}`,
    };

    useEffect(() => {
        formFieldsRef.current = formFields;
    }, [formFields]);

    const hideLoadPanel = useCallback(() => setLoadPanelVisible(false), []);

    const showErrors = (message) => {
        const errorHtml = (message || "").replace(/\\r\\n|\r\n|\n/g, "<br/>");
        custom({
            title: "Errors",
            messageHtml: errorHtml || MESSAGES.UNKNOWN_ERROR_OCCURRED,
            buttons: [{ text: "OK", onClick: () => { } }],
        }).show();
    };

    // Populate the Entry Mode dropdown once both the options and the widget exist.
    const applyEntryModeOptions = (refs) => {
        const ref = refs?.current?.["EntryMode"];
        if (ref && typeof ref.instance === "function") {
            const inst = ref.instance();
            if (inst && typeof inst.option === "function") {
                inst.option("dataSource", entryModesRef.current);
                inst.option("valueExpr", "code");
                inst.option("displayExpr", "name");
                if (formFieldsRef.current?.EntryMode) {
                    inst.option("value", formFieldsRef.current.EntryMode);
                }
            }
        }
    };

    const setFieldDisabled = (refs, name, disabled) => {
        const ref = refs?.current?.[name];
        if (ref && typeof ref.instance === "function") {
            const inst = ref.instance();
            if (inst && typeof inst.option === "function") {
                inst.option("disabled", disabled);
            }
        }
    };

    // Ports Delphi edtEntryModeChange. The three entry modes are:
    //   "Allow To Enter Start & End Date, Times"         -> enter Start/End date-time
    //   "Allow Only To Enter Start & End Times ..."      -> enter Start/End (times)
    //   "Allow Only To Enter Elapsed Hours & Minutes ..." -> enter Elapsed hours/minutes
    // Keyed off the description ("Elapsed") so it is independent of flag code/order.
    // Runs on form load, on record load (get), and on Entry Mode change.
    const applyEntryModeLogic = (refs, modeValue) => {
        if (!refs) {
            return;
        }
        const modes = entryModesRef.current || [];
        const currentMode = modeValue === undefined ? formFieldsRef.current?.EntryMode : modeValue;
        const selected = modes.find((m) => m.code === currentMode);
        const isElapsed = (selected?.name || "").toLowerCase().includes("elapsed");

        setFieldDisabled(refs, "StartDate", isElapsed);
        setFieldDisabled(refs, "EndDate", isElapsed);
        setFieldDisabled(refs, "ElapsedHours", !isElapsed);
        setFieldDisabled(refs, "ElapsedMinutes", !isElapsed);
    };

    const handlePrimaryFormLoad = useCallback((refs) => {
        primaryFormRefs.current = refs;
        applyEntryModeOptions(refs);
        applyEntryModeLogic(refs);
    }, []);

    // Entry Mode drives which fields are enabled (Start/End vs Elapsed).
    const handleFieldChange = useCallback((name, value, e, refs) => {
        if (name === "EntryMode") {
            applyEntryModeLogic(refs, value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getById = (rowId) => {
        setLoadPanelVisible(true);
        axios
            .get(`${window._env_.REACT_APP_APIS}/${routes.TransactionLaborTracking.GetLaborTracking}?rowId=${rowId}`, { headers })
            .then((res) => {
                if (res.data && res.data.rowid) {
                    setformFields(res.data);
                    applyEntryModeOptions(primaryFormRefs.current);
                    applyEntryModeLogic(primaryFormRefs.current, res.data.EntryMode);
                } else {
                    console.error(MESSAGES.FETCH_ERROR);
                }
            })
            .catch((err) => console.error(MESSAGES.FETCH_ERROR, err))
            .finally(() => setTimeout(hideLoadPanel, 500));
    };

    // Edit mode: preload the record once the row id is available.
    useEffect(() => {
        if (editRowId) {
            getById(editRowId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editRowId]);

    const goBack = () => history.push(LaborTrackingRoutes.List);

    const postTransaction = async () => {
        try {
            setLoadPanelVisible(true);
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.TransactionLaborTracking.InsertLaborTracking}`,
                formFields,
                { headers }
            );
            if (response.data.issuccess) {
                notify({ message: MESSAGES.CREATE_SUCCESS, width: 300 }, "success", 3000);
                goBack();
            } else {
                showErrors(response.data.message);
            }
        } catch (error) {
            showErrors(error?.response?.data?.message);
        } finally {
            setTimeout(hideLoadPanel, 500);
        }
    };

    const updateTransaction = async () => {
        try {
            setLoadPanelVisible(true);
            const response = await axios.put(
                `${window._env_.REACT_APP_APIS}/${routes.TransactionLaborTracking.UpdateLaborTracking}`,
                formFields,
                { headers }
            );
            if (response.data.issuccess) {
                notify({ message: MESSAGES.UPDATE_SUCCESS, width: 300 }, "success", 3000);
                goBack();
            } else {
                showErrors(response.data.message);
            }
        } catch (error) {
            showErrors(error?.response?.data?.message);
        } finally {
            setTimeout(hideLoadPanel, 500);
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (formFields.rowid === EMPTY_GUID) {
            await postTransaction();
        } else {
            await updateTransaction();
        }
    };

    const handleReset = () => {
        if (editRowId) {
            getById(editRowId);
        } else {
            setformFields(initialFormFields);
        }
    };

    const addNewRecordClick = () => {
        setformFields(initialFormFields);
        history.push(LaborTrackingRoutes.Create);
    };

    const deleteRecord = useCallback(() => {
        const result = confirm(`<i>${MESSAGES.DELETE_CONFIRM}</i>`, MESSAGES.DELETE_CONFIRMATION);
        result.then((dialogResult) => {
            if (dialogResult) {
                axios
                    .post(
                        `${window._env_.REACT_APP_APIS}/${routes.TransactionLaborTracking.DeleteLaborTrackings}`,
                        [formFields.rowid],
                        { headers: { "Content-Type": "application/json", access_token: accessToken } }
                    )
                    .then(() => {
                        notify({ message: MESSAGES.DELETE_SUCCESS, width: 300 }, "success", 3000);
                        goBack();
                    })
                    .catch(() => { });
            }
        });
    }, [formFields.rowid]);

    return (
        <div>
            <div className="row dataEntryMainPage">
                <div className="col-md-12 main-page">
                    <div id="accordion">
                        <LoadPanel
                            shadingColor="rgba(0,0,0,0.4)"
                            onHiding={hideLoadPanel}
                            visible={loadPanelVisible}
                            showIndicator={true}
                            shading={true}
                            showPane={true}
                            hideOnOutsideClick={false}
                        />
                        <form onSubmit={handleFormSubmit}>
                            <Toolbar className="header">
                                <Item location="before">
                                    <div className="title">
                                        <h6 style={{ color: window._env_.Color2, marginLeft: "12px" }}>
                                            Enter Labor Tracking
                                        </h6>
                                    </div>
                                </Item>
                                <Item location="after">
                                    <Button text="Save" useSubmitBehavior={true} type="default" className="accordianButton" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={addNewRecordClick} text="New" type="default" />
                                </Item>
                                <Item location="after">
                                    <Button
                                        text="Delete"
                                        type="default"
                                        className="accordianButton"
                                        onClick={deleteRecord}
                                        disabled={formFields.rowid === EMPTY_GUID}
                                    />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={handleReset} text="Reset" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={goBack} text="Back" />
                                </Item>
                            </Toolbar>
                            <div className="AccordionItemBody">
                                <TabPanel className="TabpanelContainer">
                                    <TabItem title="Primary">
                                        <PrimaryTab
                                            formFields={formFields}
                                            setformFields={setformFields}
                                            handlePrimaryFormLoad={handlePrimaryFormLoad}
                                            handleFieldChange={handleFieldChange}
                                        />
                                    </TabItem>
                                </TabPanel>
                            </div>
                            <div className="row">
                                <ValidationSummary className="pl-3 pt-1" id="summary" />
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(LaborTrackingForm);
